import React, { useState } from "react";
import { Link2, X } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useClaudeConnectorEnabled } from "../../hooks/useClaudeConnectorEnabled";
import { ClaudeConnectorLink, ClaudeLogo } from "./ClaudeConnectorLink";
import { ClaudeProductKind } from "./claudeConnectorConfig";

export function ClaudeDiscoverSuggestion() {
  const enabled = useClaudeConnectorEnabled();
  const { user } = useAuth();
  return enabled && user ? <DiscoverSuggestion key={user.id} userId={user.id} /> : null;
}

function DiscoverSuggestion({ userId }: { userId: string }) {
  const storageKey = `plutus:claude-connector:discover-dismissed:${userId}`;
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(storageKey) === "true"; }
    catch { return false; }
  });
  if (dismissed) return null;

  return (
    <aside aria-label="Research in Claude" className="grid grid-cols-[40px_minmax(0,1fr)_28px] items-start gap-3 rounded-v7-panel border border-v7-ink/15 bg-v7-paper p-4 font-v7-sans text-v7-ink sm:grid-cols-[44px_minmax(0,1fr)_auto_28px] sm:items-center sm:gap-4 sm:px-5 sm:py-[18px]">
      <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-v7-sand sm:h-11 sm:w-11"><ClaudeLogo className="h-6 w-6" /></div>
      <div>
        <h3 className="text-[15px] font-semibold leading-snug">Go deeper on the portfolios you discover</h3>
        <p className="mt-1 text-[13px] leading-relaxed text-v7-ink-soft">Connect Plutus to research portfolios and baskets in Claude.</p>
      </div>
      <div className="col-start-2 row-start-2 sm:col-start-auto sm:row-start-auto">
        <ClaudeConnectorLink context="discover" placement="discover-row" showLogo={false} />
      </div>
      <button type="button" aria-label="Dismiss Claude suggestion" onClick={() => {
        setDismissed(true);
        try { localStorage.setItem(storageKey, "true"); }
        catch { /* Keep dismissal in memory when browser storage is unavailable. */ }
      }} className="col-start-3 row-start-1 flex h-7 w-7 items-center justify-center rounded-v7-control text-v7-ink/60 hover:bg-v7-sand hover:text-v7-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-v7-bronze-dark sm:col-start-auto sm:row-start-auto">
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </aside>
  );
}

export function ClaudeProductStrip({ kind }: { kind: ClaudeProductKind }) {
  const enabled = useClaudeConnectorEnabled();
  if (!enabled) return null;
  return (
    <aside aria-label={`Analyze this ${kind} in Claude`} className="flex flex-wrap items-center justify-between gap-5 border-y border-v7-ink/15 bg-v7-white px-5 py-4 font-v7-sans text-v7-ink">
      <div>
        <h3 className="text-base font-semibold">Analyze this {kind} in Claude</h3>
        <p className="mt-1 text-sm text-v7-ink-soft">Ask about this {kind}'s holdings and performance, then explore further in Claude.</p>
      </div>
      <ClaudeConnectorLink context={kind === "basket" ? "basket" : "product"} placement="product-strip" />
    </aside>
  );
}

export function ClaudeConnectedAppsCard() {
  const enabled = useClaudeConnectorEnabled();
  if (!enabled) return null;
  return (
    <section aria-label="Connected apps" className="flex h-full min-h-[280px] flex-col rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5 font-v7-sans text-v7-ink transition-colors hover:bg-v7-paper md:p-6">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-v7-control bg-v7-paper text-v7-bronze-dark"><Link2 className="h-5 w-5" aria-hidden="true" /></div>
        <div><h2 className="text-base font-bold">Connected apps</h2><p className="mt-1 text-sm text-v7-ink/60">Choose your AI connections.</p></div>
      </div>
      <div className="mt-5 flex items-center gap-3 border-t border-v7-ink/10 py-[18px]">
        <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-v7-panel bg-v7-paper"><ClaudeLogo className="h-6 w-6" /></div>
        <div><h3 className="text-sm font-semibold">Claude</h3><p className="text-xs text-v7-ink/60">Read-only access</p></div>
      </div>
      <p className="mb-[18px] text-[13px] leading-relaxed text-v7-ink-soft">Connect once to ask Claude about your account and create custom reports. Claude can't trade or move money.</p>
      <div className="flex items-center gap-3 border-t border-v7-ink/10 pt-4">
        <div className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-v7-panel bg-v7-paper"><img src="https://www.runplutus.com/provider-assets/1/chatgpt.svg" alt="" aria-hidden="true" className="h-6 w-6" /></div>
        <span className="flex-1 text-sm font-medium text-v7-ink-soft">ChatGPT</span>
        <span className="rounded-v7-control border border-v7-ink/15 bg-v7-paper px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-v7-ink/60">Coming soon</span>
      </div>
      <div className="mt-auto pt-5"><ClaudeConnectorLink context="settings" placement="settings-card" showLogo={false} /></div>
    </section>
  );
}
